import { createSlice } from "@reduxjs/toolkit";
import type { ModelProvider } from "../hook/modelProvider";
import * as R from "ramda";
import { ActionType, PageContentType } from "../actionTypes";
import { Package, PackageInstall, PackageMap } from "../types/package";
import { ChartMap } from "../types/chart";
import { CodeBlockMap } from "../types/codeBlock";
import { ComponentMap, initComponentMap } from "../types/component";
import {
  ServiceAccount,
  ServiceAccountMap,
  initServiceAccountMap,
} from "../types/serviceAccount";
import {
  Devcontainer,
  DevcontainerMap,
  initDevcontainerMap,
} from "../types/devcontainer";
import { Volume, VolumeMap, initVolumeMap } from "../types/volume";
import {
  ImplementationMap,
  PageImplementationsMap,
} from "../types/implementation";
import { IndentBlockSignal } from "../types/note";
import { PageToPageViewsMap, PageViewsMap } from "../types/notebook/view";
import { PageViewSettingsMap } from "../types/notebook/viewSetting";
import {
  FolderPrimaryKeyMap,
  FolderPropertyTypesMap,
  FolderSecIndexesMap,
  PagePropertyValuesMap,
} from "../types/page/property";
import { PagePropsState } from "../types/page/pageProp";
import { PageComponentMap } from "../types/page/component";
import { LogStreamMap } from "../types/logStream";
import { HttpHookMap } from "../types/httpHook";
import {
  ConnectCard,
  ConnectorType,
} from "../types/pullConnector";
import {
  GetPaymentMethodsResponse,
  GetSubscriptionResponse,
  StripeCustomer,
} from "../types/payment";
import { TableNotFoundMap, TableNotFound } from "../types/table";
import { Message } from "../types/chat";
import { ProtocolMap, initProtocolMap } from "../types/protocol";
import { SelectedPage } from "../actionTypes";
import { AiChatLoading, AiEvent, AiEventType, ConnectionStatus } from "../types/ai-event";
import { useSelector } from "react-redux";
import { RootState } from "../reducer";
import { PageView } from "../types/notebook/view";
import { PageViewSetting } from "../types/notebook/viewSetting";
import { dragIndex } from "../reducer/note/util";
import { Chart } from "../types/chart";
import { CodeBlock } from "../types/codeBlock";
import { Component } from "../types/component";
import { Protocol } from "../types/protocol";
import {
  Implementation,
  PageImplementationsMap as PageImplMap,
} from "../types/implementation";
import { LogStream } from "../types/logStream";
import { Subscription, SubscriptionMap } from "../types/subscription";

// The AI panel's History Page state (agent-chat-speed Decision 1). cursor is the agent-service's
// History Cursor for the next OLDER page (opaque), hasMore says whether one exists, loading/error
// track the session.history request, loaded says a History Page has replaced the transcript — only
// then does "no more" mean the list shows the conversation's beginning.
export interface AiHistoryState {
  cursor: string | null;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

export const INITIAL_AI_HISTORY: AiHistoryState = {
  cursor: null,
  hasMore: false,
  loading: false,
  error: null,
  loaded: false,
};

// The conversation itself — what a History Page carries. A page replaces or prepends only these;
// status events (provisioning, progress, running, idle, errors) stay where they are.
const TRANSCRIPT_TYPES: AiEventType[] = [
  AiEventType.USER_MESSAGE,
  AiEventType.AGENT_MESSAGE,
  AiEventType.TOOL_USE,
  AiEventType.TOOL_RESULT,
  AiEventType.QUESTION,
  AiEventType.APPROVAL_REQUEST,
];

// An event's identity for UPSERT and prepend de-duplication: type + server id, the same pair
// UPSERT_AI_EVENT has always matched on.
function aiEventKey(e: AiEvent): string | null {
  const id = (e as { id?: string }).id;
  return id === undefined ? null : `${e.type}:${id}`;
}

export interface AgentSessionMeta {
  "session-id": string;
  "user-id": string;
  "app-id": string;
  "created-at": number;
  "last-message-at": number;
  "model"?: string;
  // cas-commander: set with `qo write "system:#session:SESSION-ID"` and merged into the row by Qo.Agent.Session/list-by-user
  // (query-omega-oql); attention is computed by the agent service when it answers session.list (src/sessionGoal.ts classify).
  "title"?: string;
  "goal-text"?: string;
  "role"?: string;
  "attention"?: string;
  // Live state the agent service computes when it answers session.list (src/sessionListState.ts); absent from an old server.
  "devcontainer"?: string;
  "podBound"?: boolean;
  "volumeHeld"?: "active" | "idle";
  "containerName"?: string;
}

// AI panel acting identity: the logged-in user, or a service account (key held for the WS reconnect).
export type AiIdentity =
  | { kind: "self" }
  | { kind: "service-account"; serviceAccountId: string; apiKey: string; name: string };

export type ModelProviderOverride = ModelProvider | null;

// A message sent while a turn runs. It is not an AiEvent: replay, UPSERT_AI_EVENT and chatPhase never see it,
// and it becomes an ordinary USER_MESSAGE only when the composer sends it.
export interface QueuedAiMessage {
  id: string;
  content: string;
}

export interface UiState {
  // app identity
  appId?: string;
  authToken?: string;
  // navigation / page selection
  selectedPage: SelectedPage;
  noWorkspaces?: boolean;
  initialPageLoaded?: boolean;
  // chat / AI
  chatId?: string;
  chatDrawerOpen: boolean;
  sideNavOpen: boolean;
  drawerWidth: string;
  messages: Message[];
  chatLoading: boolean;
  // AI panel
  aiPanelOpen: boolean;
  aiPanelFullscreen: boolean;
  aiEvents: AiEvent[];
  aiConnectionStatus: ConnectionStatus;
  aiHistory: AiHistoryState;
  aiChatLoading: AiChatLoading;
  // Messages waiting for the running turn to end, oldest first (QueuedAiMessage).
  aiQueued: QueuedAiMessage[];
  // OpenRouter model selection
  selectedModel: string;
  // Provider picker: null = Account setting (send no modelProvider)
  selectedProvider: ModelProviderOverride;
  // Devcontainer selection (U.2/U.3) — the block path <page>@<name>
  selectedDevcontainer: string | null;
  // AI panel identity (self or a service account)
  aiIdentity: AiIdentity;
  // Session history
  sessionHistory: AgentSessionMeta[];
  // False until the first session.list answer; the sidebar shows a loading row until then.
  sessionHistoryLoaded: boolean;
  activeSessionId: string | null;
  // content maps
  codeBlocks: CodeBlockMap;
  charts: ChartMap;
  components: ComponentMap;
  serviceAccounts: ServiceAccountMap;
  devcontainers: DevcontainerMap;
  volumes: VolumeMap;
  protocols: ProtocolMap;
  implementations: ImplementationMap;
  pageComponents: PageComponentMap;
  pageImplementations: PageImplementationsMap;
  logStreamMap: LogStreamMap;
  httpHookMap: HttpHookMap;
  // views
  pageViews: PageViewsMap;
  pageToPageViews: PageToPageViewsMap;
  selectedViewId?: string;
  pageViewSettings: PageViewSettingsMap;
  // properties
  folderPropertyTypes: FolderPropertyTypesMap;
  folderPrimaryKeys: FolderPrimaryKeyMap;
  folderSecIndexes: FolderSecIndexesMap;
  pagePropertyValues: PagePropertyValuesMap;
  pageProps: PagePropsState;
  selectedPropertyName?: string;
  // keyboard
  isShiftDown: boolean;
  // pull connector UI state (not in connectors slice)
  connectorTables: any;
  selectedPullConnector?: string;
  selectedConnectorType?: ConnectorType;
  selectedConnectCard?: ConnectCard;
  selectedPushConnector?: string;
  // payment / billing
  stripeCustomer?: StripeCustomer;
  paymentMethods?: GetPaymentMethodsResponse;
  stripeSubscription?: GetSubscriptionResponse;
  // tables not found
  notFoundTables: TableNotFoundMap;
  // misc
  darkMode: boolean;
  indentBlock?: IndentBlockSignal;
  // packages
  packages: PackageMap;
  installedPackages: PackageInstall[];
  // subscriptions
  subscriptionMap: SubscriptionMap;
  subscriptionRefreshKey: number;
  // load errors, keyed by loadKey(kind, id); a key is present only while that fetch's last attempt failed
  loadErrors: Record<string, string>;
}

const initialState: UiState = {
  appId: undefined,
  authToken: undefined,
  selectedPage: { contentType: PageContentType.LOADING_ACCOUNT },
  noWorkspaces: undefined,
  initialPageLoaded: undefined,
  loadErrors: {},
  chatId: undefined,
  chatDrawerOpen: false,
  sideNavOpen: false,
  drawerWidth: "15rem",
  messages: [],
  chatLoading: true,
  aiPanelOpen: false,
  aiPanelFullscreen: false,
  aiEvents: [],
  aiConnectionStatus: ConnectionStatus.DISCONNECTED,
  aiHistory: INITIAL_AI_HISTORY,
  aiChatLoading: null,
  aiQueued: [],
  selectedModel: "deepseek/deepseek-v4-flash",
  selectedProvider: null,
  selectedDevcontainer: localStorage.getItem("ai_selected_devcontainer") || null,
  aiIdentity: { kind: "self" },
  sessionHistory: [],
  sessionHistoryLoaded: false,
  activeSessionId: null,
  codeBlocks: {},
  charts: {},
  components: initComponentMap,
  serviceAccounts: initServiceAccountMap,
  devcontainers: initDevcontainerMap,
  volumes: initVolumeMap,
  protocols: initProtocolMap,
  implementations: {},
  pageComponents: {},
  pageImplementations: {},
  logStreamMap: {},
  httpHookMap: {},
  pageViews: {},
  pageToPageViews: {},
  selectedViewId: undefined,
  pageViewSettings: {},
  folderPropertyTypes: {},
  folderPrimaryKeys: {},
  folderSecIndexes: {},
  pagePropertyValues: {},
  pageProps: {},
  selectedPropertyName: undefined,
  isShiftDown: false,
  connectorTables: {},
  selectedPullConnector: undefined,
  selectedConnectorType: undefined,
  selectedConnectCard: undefined,
  selectedPushConnector: undefined,
  stripeCustomer: undefined,
  paymentMethods: undefined,
  stripeSubscription: undefined,
  notFoundTables: {},
  darkMode: false,
  indentBlock: undefined,
  packages: {},
  installedPackages: [],
  subscriptionMap: {},
  subscriptionRefreshKey: 0,
};

export const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // SET_APP_ID
    builder.addCase(ActionType.SET_APP_ID, (state, action: any) => {
      return { ...state, appId: action.appId };
    });

    // SET_AUTH_TOKEN
    builder.addCase(ActionType.SET_AUTH_TOKEN, (state, action: any) => {
      return { ...state, authToken: action.authToken };
    });

    // SET_SELECTED_PAGE
    builder.addCase(ActionType.SET_SELECTED_PAGE, (state, action: any) => {
      return { ...state, selectedPage: action.selectedPage };
    });

    // SET_NO_WORKSPACES
    builder.addCase(ActionType.SET_NO_WORKSPACES, (state, action: any) => {
      return action.noWorkspaces
        ? {
            ...state,
            noWorkspaces: action.noWorkspaces,
            selectedPage: { contentType: PageContentType.NO_WORKSPACE },
          }
        : { ...state, noWorkspaces: action.noWorkspaces };
    });

    // SET_INITIAL_PAGE_LOADING
    builder.addCase(
      ActionType.SET_INITIAL_PAGE_LOADING,
      (state, action: any) => {
        return { ...state, initialPageLoaded: action.initialPageLoaded };
      },
    );

    // SET_CHAT_ID
    builder.addCase(ActionType.SET_CHAT_ID, (state, action: any) => {
      return { ...state, chatId: action.chatId };
    });

    // SET_LOAD_ERROR
    builder.addCase(ActionType.SET_LOAD_ERROR, (state, action: any) => {
      return { ...state, loadErrors: { ...state.loadErrors, [action.key]: action.message } };
    });

    // CLEAR_LOAD_ERROR
    builder.addCase(ActionType.CLEAR_LOAD_ERROR, (state, action: any) => {
      const { [action.key]: _removed, ...rest } = state.loadErrors;
      return { ...state, loadErrors: rest };
    });

    // SET_CHAT_DRAWER_OPEN
    builder.addCase(ActionType.SET_CHAT_DRAWER_OPEN, (state, action: any) => {
      return { ...state, chatDrawerOpen: action.chatDrawerOpen };
    });

    // SET_SIDE_NAV_OPEN
    builder.addCase(ActionType.SET_SIDE_NAV_OPEN, (state, action: any) => {
      return { ...state, sideNavOpen: action.sideNavOpen };
    });

    // ADD_MESSAGE
    builder.addCase(ActionType.ADD_MESSAGE, (state, action: any) => {
      return { ...state, messages: [...state.messages, action.message] };
    });

    // SET_MESSAGES
    builder.addCase(ActionType.SET_MESSAGES, (state, action: any) => {
      return { ...state, messages: action.messages };
    });

    // SET_CHAT_LOADING
    builder.addCase(ActionType.SET_CHAT_LOADING, (state, action: any) => {
      return { ...state, chatLoading: action.chatLoading };
    });

    // SET_AI_PANEL_OPEN
    builder.addCase(ActionType.SET_AI_PANEL_OPEN, (state, action: any) => {
      return { ...state, aiPanelOpen: action.aiPanelOpen };
    });

    // SET_AI_PANEL_FULLSCREEN
    builder.addCase(
      ActionType.SET_AI_PANEL_FULLSCREEN,
      (state, action: any) => {
        return { ...state, aiPanelFullscreen: action.aiPanelFullscreen };
      },
    );

    // ADD_AI_EVENT
    builder.addCase(ActionType.ADD_AI_EVENT, (state, action: any) => {
      return { ...state, aiEvents: [...state.aiEvents, action.event] };
    });

    // UPSERT_AI_EVENT — scans from the END: a live update (a streamed text delta, a tool call's
    // next state) targets one of the newest events, so the scan stops after a few steps instead of
    // walking the whole transcript (agent-chat-speed Decision 3). No id→index map: the array copy
    // below is O(n) whatever finds the index, and a map in state would be copied too.
    builder.addCase(ActionType.UPSERT_AI_EVENT, (state, action: any) => {
      const key = aiEventKey(action.event);
      let i = -1;
      if (key !== null) {
        for (let j = state.aiEvents.length - 1; j >= 0; j--) {
          if (aiEventKey(state.aiEvents[j]) === key) {
            i = j;
            break;
          }
        }
      }
      if (i < 0) return { ...state, aiEvents: [...state.aiEvents, action.event] };
      const next = state.aiEvents.slice();
      next[i] = action.event;
      return { ...state, aiEvents: next };
    });

    // SET_AI_EVENTS
    builder.addCase(ActionType.SET_AI_EVENTS, (state, action: any) => {
      return { ...state, aiEvents: action.events };
    });

    // SET_AI_EVENTS_BATCH — the newest History Page of a resumed chat (session.replay, prepend
    // false): replaces the transcript in ONE update (agent-chat-speed Decision 2) and keeps the
    // status events already there — the open's provisioning hold and its progress lines — after it.
    builder.addCase(ActionType.SET_AI_EVENTS_BATCH, (state, action: any) => {
      const kept = state.aiEvents.filter((e) => TRANSCRIPT_TYPES.indexOf(e.type) < 0);
      return {
        ...state,
        aiEvents: [...action.events, ...kept],
        aiHistory: { cursor: action.cursor, hasMore: action.hasMore, loading: false, error: null, loaded: true },
        aiChatLoading: null,
      };
    });

    // PREPEND_AI_EVENTS — an older History Page (session.replay, prepend true) goes above the
    // transcript; an event already on screen (same type + id) is not added twice.
    builder.addCase(ActionType.PREPEND_AI_EVENTS, (state, action: any) => {
      const present: Record<string, true> = {};
      state.aiEvents.forEach((e) => {
        const k = aiEventKey(e);
        if (k !== null) present[k] = true;
      });
      const older = (action.events as AiEvent[]).filter((e) => {
        const k = aiEventKey(e);
        return k === null || !present[k];
      });
      return {
        ...state,
        aiEvents: [...older, ...state.aiEvents],
        aiHistory: { cursor: action.cursor, hasMore: action.hasMore, loading: false, error: null, loaded: true },
        aiChatLoading: null,
      };
    });

    // SET_AI_HISTORY_LOADING — a session.history request went out.
    builder.addCase(ActionType.SET_AI_HISTORY_LOADING, (state) => {
      return { ...state, aiHistory: { ...state.aiHistory, loading: true, error: null } };
    });

    // SET_AI_HISTORY_ERROR — session.history_error (or the request could not be sent); the loaded
    // events stay as they are.
    builder.addCase(ActionType.SET_AI_HISTORY_ERROR, (state, action: any) => {
      return { ...state, aiHistory: { ...state.aiHistory, loading: false, error: action.error } };
    });

    // CLEAR_AI_EVENTS — a new transcript starts with no History Page state.
    builder.addCase(ActionType.CLEAR_AI_EVENTS, (state) => {
      return { ...state, aiEvents: [], aiHistory: INITIAL_AI_HISTORY, aiChatLoading: null, aiQueued: [] };
    });

    // ENQUEUE_AI_MESSAGE — append to the queue; the composer drains it one message per turn.
    builder.addCase(ActionType.ENQUEUE_AI_MESSAGE, (state, action: any) => {
      return { ...state, aiQueued: [...state.aiQueued, { id: action.id, content: action.content }] };
    });

    // DEQUEUE_AI_MESSAGE — the composer takes the first message to send it.
    builder.addCase(ActionType.DEQUEUE_AI_MESSAGE, (state) => {
      return { ...state, aiQueued: state.aiQueued.slice(1) };
    });

    // CANCEL_AI_QUEUED — the cancel control on one queued bubble.
    builder.addCase(ActionType.CANCEL_AI_QUEUED, (state, action: any) => {
      return { ...state, aiQueued: state.aiQueued.filter((m) => m.id !== action.id) };
    });

    // SET_AI_CONNECTION_STATUS
    builder.addCase(
      ActionType.SET_AI_CONNECTION_STATUS,
      (state, action: any) => {
        return { ...state, aiConnectionStatus: action.status };
      },
    );

    // SET_AI_CHAT_LOADING — the resume/history marker that drives the skeleton phase.
    builder.addCase(ActionType.SET_AI_CHAT_LOADING, (state, action: any) => {
      return { ...state, aiChatLoading: action.loading ?? null };
    });

    // SET_SESSION_HISTORY
    builder.addCase(ActionType.SET_SESSION_HISTORY, (state, action: any) => {
      return { ...state, sessionHistory: action.sessions, sessionHistoryLoaded: action.loaded ?? true };
    });

    // SET_ACTIVE_SESSION_ID
    builder.addCase(ActionType.SET_ACTIVE_SESSION_ID, (state, action: any) => {
      return { ...state, activeSessionId: action.sessionId };
    });

    // SET_SELECTED_MODEL
    builder.addCase(ActionType.SET_SELECTED_MODEL, (state, action: any) => {
      return { ...state, selectedModel: action.model };
    });

    // The picker's override — in memory only: the Account setting sends no modelProvider.
    builder.addCase(ActionType.SET_SELECTED_PROVIDER, (state, action: any) => {
      return {
        ...state,
        selectedProvider: (action.provider ?? null) as ModelProviderOverride,
      };
    });

    // SET_AI_IDENTITY
    builder.addCase(ActionType.SET_AI_IDENTITY, (state, action: any) => {
      return { ...state, aiIdentity: action.identity };
    });

    // SET_SELECTED_DEVCONTAINER
    builder.addCase(ActionType.SET_SELECTED_DEVCONTAINER, (state, action: any) => {
      if (action.devcontainer) {
        localStorage.setItem("ai_selected_devcontainer", action.devcontainer);
      } else {
        localStorage.removeItem("ai_selected_devcontainer");
      }
      return { ...state, selectedDevcontainer: action.devcontainer ?? null };
    });

    // ADD_CODE_BLOCKS
    builder.addCase(ActionType.ADD_CODE_BLOCKS, (state, action: any) => {
      const codeBlocksAction: CodeBlock[] = action.codeBlocks;
      const codeBlocksActionMap = Object.fromEntries(
        codeBlocksAction.map((cb) => [cb["code-block-id"], cb]),
      );
      return {
        ...state,
        codeBlocks: { ...state.codeBlocks, ...codeBlocksActionMap },
      };
    });

    // ADD_CHARTS
    builder.addCase(ActionType.ADD_CHARTS, (state, action: any) => {
      const chartsAction: Chart[] = action.charts;
      const chartsMapAction = Object.fromEntries(
        chartsAction.map((chart) => [chart["chart-id"], chart]),
      );
      return { ...state, charts: { ...state.charts, ...chartsMapAction } };
    });

    // ADD_COMPONENTS
    builder.addCase(ActionType.ADD_COMPONENTS, (state, action: any) => {
      const componentsFromAction: Component[] = action.components;
      const newEntries = Object.fromEntries(
        componentsFromAction.map((comp) => [comp["component-id"], comp]),
      );
      return { ...state, components: { ...state.components, ...newEntries } };
    });

    // ADD_SERVICE_ACCOUNTS (merge by service-account-id)
    builder.addCase(ActionType.ADD_SERVICE_ACCOUNTS, (state, action: any) => {
      const serviceAccountsFromAction: ServiceAccount[] =
        action.serviceAccounts;
      const newEntries = Object.fromEntries(
        serviceAccountsFromAction.map((sa) => [sa["service-account-id"], sa]),
      );
      return {
        ...state,
        serviceAccounts: { ...state.serviceAccounts, ...newEntries },
      };
    });

    // ADD_DEVCONTAINERS (merge by devcontainer-id)
    builder.addCase(ActionType.ADD_DEVCONTAINERS, (state, action: any) => {
      const items: Devcontainer[] = action.devcontainers;
      const newEntries = Object.fromEntries(
        items.map((d) => [d["devcontainer-id"], d]),
      );
      return {
        ...state,
        devcontainers: { ...state.devcontainers, ...newEntries },
      };
    });

    // ADD_VOLUMES (merge by volume-id)
    builder.addCase(ActionType.ADD_VOLUMES, (state, action: any) => {
      const items: Volume[] = action.volumes;
      const newEntries = Object.fromEntries(
        items.map((v) => [v["volume-id"], v]),
      );
      return {
        ...state,
        volumes: { ...state.volumes, ...newEntries },
      };
    });

    // ADD_PROTOCOLS
    builder.addCase(ActionType.ADD_PROTOCOLS, (state, action: any) => {
      const protocolsFromAction: Protocol[] = action.protocols;
      const newEntries = Object.fromEntries(
        protocolsFromAction.map((proto) => [proto["protocol-id"], proto]),
      );
      return { ...state, protocols: { ...state.protocols, ...newEntries } };
    });

    // ADD_IMPLEMENTATIONS
    builder.addCase(ActionType.ADD_IMPLEMENTATIONS, (state, action: any) => {
      const implementationsFromAction: Implementation[] = action.implementations;
      const newEntries = Object.fromEntries(
        implementationsFromAction.map((impl) => [
          impl["implementation-id"],
          impl,
        ]),
      );
      return {
        ...state,
        implementations: { ...state.implementations, ...newEntries },
      };
    });

    // ADD_PAGE_IMPLEMENTATIONS
    builder.addCase(
      ActionType.ADD_PAGE_IMPLEMENTATIONS,
      (state, action: any) => {
        const { implementations, pageId }: { implementations: Implementation[]; pageId: string } =
          action;
        const newEntries = Object.fromEntries(
          implementations.map((impl) => [impl["implementation-id"], impl]),
        );
        const implIds = implementations.map((impl) => impl["implementation-id"]);
        return {
          ...state,
          implementations: { ...state.implementations, ...newEntries },
          pageImplementations: { ...state.pageImplementations, [pageId]: implIds },
        };
      },
    );

    // SET_PAGE_COMPONENTS
    builder.addCase(ActionType.SET_PAGE_COMPONENTS, (state, action: any) => {
      const { pageComponents: pageComponentsAction } = action;
      return {
        ...state,
        pageComponents: { ...state.pageComponents, ...pageComponentsAction },
      };
    });

    // ADD_LOG_STREAMS
    builder.addCase(ActionType.ADD_LOG_STREAMS, (state, action: any) => {
      const logStreams: LogStream[] = action.logStreams;
      const logStreamEntries = Object.fromEntries(
        logStreams.map((ls) => [ls["log-stream-id"], ls]),
      );
      return {
        ...state,
        logStreamMap: { ...state.logStreamMap, ...logStreamEntries },
      };
    });

    // SET_PAGE_PAGE_VIEWS
    builder.addCase(ActionType.SET_PAGE_PAGE_VIEWS, (state, action: any) => {
      const { pageId, pageViews: pageViewsData }: { pageId: string; pageViews: PageView[] } =
        action;
      const pageViewEntries = Object.fromEntries(
        pageViewsData.map((pv) => [pv["view-id"], pv]),
      );
      return {
        ...state,
        pageViews: { ...state.pageViews, ...pageViewEntries },
        pageToPageViews: { ...state.pageToPageViews, [pageId]: pageViewsData },
      };
    });

    // SET_SELECTED_VIEW_ID
    builder.addCase(ActionType.SET_SELECTED_VIEW_ID, (state, action: any) => {
      return { ...state, selectedViewId: action.viewId };
    });

    // SWAP_DATABASE_VIEWS
    builder.addCase(ActionType.SWAP_DATABASE_VIEWS, (state, action: any) => {
      const { active, over }: { active: PageView; over: PageView } = action;
      const pageId = active["page-id"];
      const views = state.pageToPageViews[pageId];
      const activeIndex = views.findIndex(
        (v) => v["view-id"] === active["view-id"],
      );
      const overIndex = views.findIndex(
        (v) => v["view-id"] === over["view-id"],
      );
      const newViews = dragIndex(views, activeIndex, overIndex);
      return {
        ...state,
        pageToPageViews: { ...state.pageToPageViews, [pageId]: newViews },
      };
    });

    // SET_VIEW_SETTING
    builder.addCase(ActionType.SET_VIEW_SETTING, (state, action: any) => {
      const { viewId, setting }: { viewId: string; setting: PageViewSetting } = action;
      return {
        ...state,
        pageViewSettings: { ...state.pageViewSettings, [viewId]: setting },
      };
    });

    // SET_FOLDER_PROPERTIES
    builder.addCase(ActionType.SET_FOLDER_PROPERTIES, (state, action: any) => {
      return {
        ...state,
        folderPropertyTypes: {
          ...state.folderPropertyTypes,
          ...action.folderProps,
        },
      };
    });

    // SET_FOLDER_PRIMARY_KEY
    builder.addCase(ActionType.SET_FOLDER_PRIMARY_KEY, (state, action: any) => {
      const { folderId, pKey } = action;
      return {
        ...state,
        folderPrimaryKeys: { ...state.folderPrimaryKeys, [folderId]: pKey },
      };
    });

    // SET_FOLDER_SEC_INDEXES
    builder.addCase(
      ActionType.SET_FOLDER_SEC_INDEXES,
      (state, action: any) => {
        const { folderId, secIndexes } = action;
        return {
          ...state,
          folderSecIndexes: {
            ...state.folderSecIndexes,
            [folderId]: secIndexes,
          },
        };
      },
    );

    // SET_PAGE_PROPERTY_VALUES
    builder.addCase(
      ActionType.SET_PAGE_PROPERTY_VALUES,
      (state, action: any) => {
        return {
          ...state,
          pagePropertyValues: {
            ...state.pagePropertyValues,
            ...action.propVals,
          },
        };
      },
    );

    // SET_PAGE_PROPS
    builder.addCase(ActionType.SET_PAGE_PROPS, (state, action: any) => {
      const { pageId, props } = action;
      state.pageProps = { ...(state.pageProps ?? {}), [pageId]: props };
    });

    // DELETE_PROPERTY
    builder.addCase(ActionType.DELETE_PROPERTY, (state, action: any) => {
      const { propName, folderId } = action;
      const folderProps = state.folderPropertyTypes[folderId] ?? {};
      const newFolderProps = R.dissoc(propName, folderProps);
      return {
        ...state,
        folderPropertyTypes: {
          ...state.folderPropertyTypes,
          [folderId]: newFolderProps,
        },
      };
    });

    // SET_SELECTED_PROPERTY
    builder.addCase(ActionType.SET_SELECTED_PROPERTY, (state, action: any) => {
      return { ...state, selectedPropertyName: action.propId };
    });

    // SET_SHIFT_DOWN
    builder.addCase(ActionType.SET_SHIFT_DOWN, (state, action: any) => {
      return { ...state, isShiftDown: action.isShiftDown };
    });

    // ASSOCIATE_CONNECTOR_TABLES
    builder.addCase(
      ActionType.ASSOCIATE_CONNECTOR_TABLES,
      (state, action: any) => {
        return {
          ...state,
          connectorTables: {
            ...state.connectorTables,
            ...action.connectorTables,
          },
        };
      },
    );

    // SET_SELECTED_PULL_CONNECTOR
    builder.addCase(
      ActionType.SET_SELECTED_PULL_CONNECTOR,
      (state, action: any) => {
        return {
          ...state,
          selectedPullConnector: action.selectedPullConnector,
        };
      },
    );

    // SET_SELECTED_CONNECTOR_TYPE
    builder.addCase(
      ActionType.SET_SELECTED_CONNECTOR_TYPE,
      (state, action: any) => {
        return {
          ...state,
          selectedConnectorType: action.selectedConnectorType,
        };
      },
    );

    // SET_SELECTED_CONNECT_CARD
    builder.addCase(
      ActionType.SET_SELECTED_CONNECT_CARD,
      (state, action: any) => {
        return { ...state, selectedConnectCard: action.selectedConnectCard };
      },
    );

    // SET_SELECTED_PUSH_CONNECTOR
    builder.addCase(
      ActionType.SET_SELECTED_PUSH_CONNECTOR,
      (state, action: any) => {
        return {
          ...state,
          selectedPushConnector: action.selectedPushConnector,
          selectedPage: { contentType: PageContentType.VIEW_PUSH_CONNECTOR },
        };
      },
    );

    // SET_STRIPE_CUSTOMER
    builder.addCase(ActionType.SET_STRIPE_CUSTOMER, (state, action: any) => {
      return { ...state, stripeCustomer: action.stripeCustomer };
    });

    // SET_PAYMENT_METHODS
    builder.addCase(ActionType.SET_PAYMENT_METHODS, (state, action: any) => {
      return { ...state, paymentMethods: action.paymentMethods };
    });

    // SET_STRIPE_SUBSCRIPTION
    builder.addCase(
      ActionType.SET_STRIPE_SUBSCRIPTION,
      (state, action: any) => {
        return { ...state, stripeSubscription: action.stripeSubscription };
      },
    );

    // ADD_NOT_FOUND_TABLES
    builder.addCase(ActionType.ADD_NOT_FOUND_TABLES, (state, action: any) => {
      const notFoundTables = action.notFoundTables as TableNotFound[];
      const notFoundEntries = Object.fromEntries(
        notFoundTables.map((t) => [t["table-id"], t]),
      );
      return {
        ...state,
        notFoundTables: { ...state.notFoundTables, ...notFoundEntries },
      };
    });

    // ADD_PACKAGES
    builder.addCase(ActionType.ADD_PACKAGES, (state, action: any) => {
      const pkgs = action.packages as Package[];
      const newEntries: PackageMap = Object.fromEntries(
        pkgs.map((pkg) => [pkg["package-id"], pkg]),
      );
      return { ...state, packages: { ...state.packages, ...newEntries } };
    });

    // SET_INSTALLED_PACKAGES
    builder.addCase(ActionType.SET_INSTALLED_PACKAGES, (state, action: any) => {
      return { ...state, installedPackages: action.installs as PackageInstall[] };
    });

    // SET_SELECTED_NOTEBOOK_PAGE_ID
    builder.addCase(ActionType.SET_SELECTED_NOTEBOOK_PAGE_ID, (state) => {
      return { ...state, selectedViewId: undefined };
    });

    // ADD_SUBSCRIPTIONS
    builder.addCase(ActionType.ADD_SUBSCRIPTIONS, (state, action: any) => {
      const subs: Subscription[] = action.subscriptions;
      const newEntries: SubscriptionMap = Object.fromEntries(
        subs.map((s) => [s["sub-id"], s]),
      );
      return { ...state, subscriptionMap: { ...state.subscriptionMap, ...newEntries } };
    });

    // CLEAR_SUBSCRIPTION_MAP
    builder.addCase(ActionType.CLEAR_SUBSCRIPTION_MAP, (state) => {
      return { ...state, subscriptionMap: {} };
    });

    // INCREMENT_SUBSCRIPTION_REFRESH
    builder.addCase(ActionType.INCREMENT_SUBSCRIPTION_REFRESH, (state) => {
      return { ...state, subscriptionRefreshKey: state.subscriptionRefreshKey + 1 };
    });

    // ADD_HTTP_HOOKS
    builder.addCase(ActionType.ADD_HTTP_HOOKS, (state, action: any) => {
      const httpHooks: import("../types/httpHook").HttpHook[] = action.httpHooks;
      const newEntries: HttpHookMap = Object.fromEntries(
        httpHooks.map((h) => [h["hook-id"], h]),
      );
      return { ...state, httpHookMap: { ...state.httpHookMap, ...newEntries } };
    });
  },
});

export default uiSlice.reducer;

// Selector hooks — mirror the useAiEvents style in hook/ai-panel.ts
export function useSessionHistory(): AgentSessionMeta[] {
  return useSelector((state: RootState) => state.ui.sessionHistory);
}

export function useSessionHistoryLoaded(): boolean {
  return useSelector((state: RootState) => state.ui.sessionHistoryLoaded);
}

export function useActiveSessionId(): string | null {
  return useSelector((state: RootState) => state.ui.activeSessionId);
}

export function useAiIdentity(): AiIdentity {
  return useSelector((state: RootState) => state.ui.aiIdentity);
}

export function useAiChatLoading(): AiChatLoading {
  return useSelector((state: RootState) => state.ui.aiChatLoading);
}

export function useLoadError(key: string | undefined): string | undefined {
  return useSelector((state: RootState) => (key === undefined ? undefined : state.ui.loadErrors[key]));
}
